import type {DynamicQuestionNode as DynamicQuestionNodeType} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useChatbotStateContext} from '../chatbot-state-context';
import {useEditorState} from '../node-editor/reducer';
import {validateNode} from '../node-editor/validation';

import {createDynamicQuestionNode, createEditableGraph} from '../utils';
import classify from 'src/utils/classify';

import {useWritebackAllowedAttributesV2} from 'src/hooks/useWritebackAllowedAttributes';

import {Button} from 'src/designSystem2021Components/buttons';
import {
  Header,
  Smallest,
  Paragraph,
} from 'src/designSystem2021Components/text-v2';
import {Input} from 'src/designSystem2021Components/input';

import CloseIcon from 'src/images/designSystems2021/close.svg';

import sidePanelCss from 'src/components/modals/generic-sidepanel.css';
import css from './node-editor-v2.css';
import {type AttributeMetaData} from 'src/types/writeback';


export function DynamicQuestionNode({
  graph,
  onCancel,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {t, makeKey} = useLabelTranslation();
  const editableGraph = createEditableGraph(graph);

  const writebackAttributes = useWritebackAllowedAttributesV2(
    graph.anchor_entity_type,
  );
  const {state, dispatch} = useChatbotStateContext();

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);

  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'dynamic_question'
      ? editingNodeData
      : createDynamicQuestionNode();
  const [editorState, editorDispatch] = useEditorState<DynamicQuestionNodeType>(
    {
      defaultNode,
    },
  );
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const {node} = editorState;

  const setNode = (payload: Partial<DynamicQuestionNodeType>) => {
    editorDispatch({type: 'update', payload});
  };

  const validation = validateNode(node, editableGraph, null);

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  React.useEffect(() => {
    const writebackAttrs = writebackAttributes?.attribute_meta_data ?? [];
    if (node.writeback_field_id == null && writebackAttrs.length > 0) {
      // get qa fields
      const qaField = writebackAttrs.find(
        (attr) => attr.data_type === 'question_answer',
      );
      if (qaField) {
        setNode({writeback_field_id: qaField.field_id});
      }
    }
  }, [writebackAttributes]);

  const error = hasSubmitted ? validation : null;

  return (
    <div className={css.questionNodeContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
        {error?.name && (
          <div className={css.errorText}>
            {error.name
              ? error.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum',
                )}.`}
          </div>
        )}
      </div>
      <Smallest className={css.section}>{`${t(
        'Dynamic_questions_aren\'t_currently_editable_but_you_can_change_their_name',
        'Dynamic questions aren\'t currently editable, but you can change their name',
      )}.`}</Smallest>
      {node.writeback_field_id != null && (
        <Paragraph className={css.section}>
          {t(
            'This_qa_node_will_write_back_to',
            'This q&a node will write back to',
          )}{' '}
          <strong>
            {t(
              makeKey(
                writebackAttributes?.attribute_meta_data.find(
                  (m) => m.field_id === node.writeback_field_id,
                )?.display_name ?? '',
              ),
              writebackAttributes?.attribute_meta_data.find(
                (m: AttributeMetaData) =>
                  m.field_id === node.writeback_field_id,
              )?.display_name ?? '',
            )}
          </strong>
        </Paragraph>
      )}
      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted && !!validation}
        >
          {t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'onCancel' implicitly has an 'any' type. | TS7031 - Binding element 'header' implicitly has an 'any' type.
const NodeHeader = ({onCancel, header}) => (
  <div className={classify(sidePanelCss.headerContainer, css.header)}>
    {header}
    <div
      className={sidePanelCss.closeIconContainer}
      onClick={() => {
        onCancel && onCancel();
      }}
    >
      <CloseIcon className={sidePanelCss.closeIcon} />
    </div>
  </div>
);
